/**
 * Legacy packageCategory tokens and newer category-page slugs diverged during
 * migration (e.g. `divinity-packages` vs `divinity-tour-packages`). These
 * helpers treat alias pairs as the same category everywhere we link destinations,
 * filter packages, or resolve homepage showcases.
 */
export const PACKAGE_CATEGORY_PAGE_ALIASES: Record<string, string> = {
  'divinity-packages': 'divinity-tour-packages',
  'cruise-holidays': 'cruise-tour-packages',
  'value-escapes': 'value-escapes-tour-packages',
  'group-holidays': 'group-tour-package',
  'group-tour-packages': 'group-tour-package',
  // Legacy int'l/domestic flags on packages map to the two routing categories.
  international: 'international-tour-holidays',
  domestic: 'indian-tour-holidays',
};

/** Tokens that are flags/promos, not real destination categories. */
export const SKIP_PACKAGE_CATEGORY_TOKENS = new Set([
  'international',
  'domestic',
  'hot-selling',
  'bnpl-card-offer',
  'last-minute-deals',
  'advance-purches-offer',
  'hotel-deals',
]);

export function normalizeCategorySlug(slug: string): string {
  return slug.toLowerCase().trim();
}

/** All category-page slugs / package tokens that refer to the same travel category. */
export function equivalentCategorySlugs(slugOrToken: string): string[] {
  const n = normalizeCategorySlug(slugOrToken);
  const out = new Set<string>([n]);
  const forward = PACKAGE_CATEGORY_PAGE_ALIASES[n];
  if (forward) out.add(normalizeCategorySlug(forward));
  for (const [from, to] of Object.entries(PACKAGE_CATEGORY_PAGE_ALIASES)) {
    if (normalizeCategorySlug(to) === n) out.add(normalizeCategorySlug(from));
  }
  return [...out];
}

/** packageCategory tokens that should match a given category page slug. */
export function packageCategoryTokensForPageSlug(categoryPageSlug: string): Set<string> {
  return new Set(equivalentCategorySlugs(categoryPageSlug));
}

/**
 * Category pages whose package listing is scoped by the operator-set trip type
 * (`TripType.code`, the "Trip category" dropdown on the package form) rather
 * than by the destination-to-category links. Only categories listed here are
 * scoped - any other slug lists across all trip types, as before.
 *
 * Keyed by canonical slug; alias variants resolve through
 * `equivalentCategorySlugs`, so `community-trip` and `community-trips` both hit
 * the same entry.
 */
export const CATEGORY_PAGE_TRIP_TYPE_CODES: Record<string, string> = {
  'community-trips': 'community',
  'community-trip': 'community',
};

/** `TripType.code` a category page's packages must carry, or null when unscoped. */
export function tripTypeCodeForCategorySlug(categoryPageSlug: string): string | null {
  for (const slug of equivalentCategorySlugs(categoryPageSlug)) {
    const code = CATEGORY_PAGE_TRIP_TYPE_CODES[slug];
    if (code) return code;
  }
  return null;
}

/**
 * Categories whose top destination circles are CRM-curated (ordered picks on the
 * category page) rather than auto-listed from every linked destination page.
 * Today only Community Trips; keep in sync with the admin "Destination circles"
 * tab gate.
 */
export function categoryPageUsesCuratedCircles(categoryPageSlug: string): boolean {
  return tripTypeCodeForCategorySlug(categoryPageSlug) === 'community';
}

/** Resolve a category page id from a slug map, trying alias variants. */
export function lookupCategoryPageId(
  slugOrToken: string,
  bySlug: ReadonlyMap<string, string>,
): string | undefined {
  for (const slug of equivalentCategorySlugs(slugOrToken)) {
    const id = bySlug.get(normalizeCategorySlug(slug));
    if (id) return id;
  }
  return undefined;
}
